import type { MouseEvent } from "react";
import type { ChargerHealth, ChargerTileState } from "../../types/charger";
import { formatPowerGauge } from "../../utils/chargerPowerGauge";
import { chargerTooltip } from "./chargerTooltip";

function healthSegClass(health: ChargerHealth): string {
  if (health === "ok") return "charger-seg--health-ok";
  if (health === "unknown") return "charger-seg--health-unknown";
  return "charger-seg--health-stale";
}

function Segment({ className }: { className: string }) {
  return <span className={["charger-seg", className].filter(Boolean).join(" ")} />;
}

function SegmentStack({ state }: { state: ChargerTileState }) {
  return (
    <div className="charger-seg-stack" aria-hidden>
      <div className="charger-seg-row">
        <Segment className={healthSegClass(state.health)} />
        <Segment className={state.mv5 ? "charger-seg--alloc-on" : ""} />
        <Segment className={state.fcrActivated ? "charger-seg--activ-on" : ""} />
      </div>
      <div className="charger-seg-row">
        <Segment className={state.sp ? "charger-seg--sp-on" : ""} />
        <Segment className={state.resp ? "charger-seg--resp-on" : ""} />
      </div>
    </div>
  );
}

function tileClass(state: ChargerTileState): string {
  if (state.health === "stale") return "charger-tile--h";
  if (state.charging) return "charger-tile--ring-blue";
  if (state.evConnected) return "charger-tile--ring-green";
  return "charger-tile--idle";
}

export function ChargerTile({
  state,
  highlighted = false,
  dimmed = false,
  onClick,
}: {
  state: ChargerTileState;
  /** Search match — ring emphasis when a find query is active. */
  highlighted?: boolean;
  /** Non-match while search is active — de-emphasize without hiding. */
  dimmed?: boolean;
  onClick?: (ev: MouseEvent<HTMLButtonElement>) => void;
}) {
  const powerLabel = formatPowerGauge(state.powerGauge);

  return (
    <button
      type="button"
      className={[
        "charger-tile",
        tileClass(state),
        highlighted ? "charger-tile--search-match" : "",
        dimmed ? "charger-tile--search-dim" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={chargerTooltip(state)}
      onClick={onClick}
    >
      <span className="charger-tile-id">{state.label}</span>
      <div className="charger-tile-foot">
        <SegmentStack state={state} />
        <span className="charger-power-gauge" aria-hidden={powerLabel == null}>
          {powerLabel ?? ""}
        </span>
      </div>
    </button>
  );
}
